import React, { ChangeEvent, useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import { useList } from '@/hooks/useList';
import { useTranslation } from '@refinedev/core';
import { PlusOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { getColumns } from './columns';
import { Form, Input, Button, Space } from 'antd';
import { AddTopicModal } from '@/components/modals';
import { TopicsDataGet } from '@/api/types/topicsDataGet';
import { EditTopicModal } from '@/components/modals/EditTopicModal';
import { useProfileStore } from '@/core';

export const TopicList = () => {
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const { translate } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<{
    id: string;
    name: string;
    sort_order: number;
  } | null>(null);
  const [form] = Form.useForm();
  const {
    data,
    isLoading,
    page,
    setPage,
    isRefetching,
    setName,
    refetch,
    resolvedTotal,
  } = useList<{
    data: TopicsDataGet[];
  }>({
    endpoint: endPoints.topicAdmin,
  });
  const openEditModal = (topic: {
    id: string;
    name: string;
    sort_order: number;
  }) => {
    setSelectedTopic(topic);
    setIsEditModalOpen(true);
  };
  const columns = getColumns(translate, refetch, openEditModal, adminType);

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setName(e.target.value);
  };

  const [isAddTopicModalOpen, setIsAddTopicModalOpen] = useState(false);

  const handleAddTopic = () => {
    setIsAddTopicModalOpen(true);
  };

  const handleResetFilters = () => {
    form.resetFields();
    setName('');
  };

  return (
    <List
      title={
        <div className="flex gap-x-3">
          <UserOutlined />
          <h1>{translate('listing-topics.title')}</h1>
        </div>
      }
      canCreate={false}
      headerButtonProps={{
        hidden:
          adminType === 'ACADEMIC_SUPERVISOR' ||
          adminType === 'PROGRAM_HEAD' ||
          adminType === 'SYSTEM_ADMIN',
      }}
      headerButtons={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTopic}
          >
            {translate('buttons.create')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <div className="flex flex-wrap gap-x-3">
          <Form.Item name="search" className="w-full sm:w-1/6 min-w-[200px]">
            <Input
              placeholder={translate('search-user.fields.search')}
              onChange={onSearch}
            />
          </Form.Item>
          <Button
            className="mb-2 sm:mb-0"
            type="primary"
            onClick={handleResetFilters}
            icon={<ReloadOutlined />}
          />
        </div>
      </Form>
      <DataTable
        pageSize={10}
        currentPage={page}
        onPageChange={setPage}
        columns={columns}
        loading={isLoading || isRefetching}
        data={{
          data: data?.data.data || [],
          headers: {
            'x-total-count': resolvedTotal,
          },
        }}
      />
      <AddTopicModal
        open={isAddTopicModalOpen}
        onCancel={() => setIsAddTopicModalOpen(false)}
        refetch={refetch}
      />
      <EditTopicModal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        refetch={refetch}
        topicId={selectedTopic?.id || ''}
        initialValues={{
          name: selectedTopic?.name || '',
          sort_order: selectedTopic?.sort_order || 0,
        }}
      />
    </List>
  );
};
